import './Navigation.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Chip, Divider } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import AcDialog from '../AcDialog/AcDialog';
import logo from '../../assets/images/logo.svg';
import { UserRoles } from '../../constants/bindings';
import { menuItems, BREAKS, SIZES } from '../../constants/nav-routing';
import { getDefaultActiveMenu } from '../../helpers/commonHelpers';
import AcDialogInterface from '../../models/AcDialogInterface';
import { useAuth } from '../../providers/Auth';
import { useAdminAppControl } from '../../providers/AdminAppControlProvider';

function Navigation() {
  const { user, signIn, signOut } = useAuth();
  const location = useLocation();
  const pathName = useLocation().pathname;
  const { t } = useTranslation();

  const [activeMenu, setActiveMenu] = useState(getDefaultActiveMenu(pathName));
  const [activeSubMenu, setActiveSubMenu] = useState('');
  const [openLogoutBox, setOpenLogoutBox] = useState(false);
  const [size, setSize] = useState(SIZES.LARGE);

  const { featureState } = useAdminAppControl();

  const baseMenu = [
    {
      title: 'login',
      icon: true,
      path: '/',
      id: 'login',
      subItems: []
    }
  ];
  const hideNavOnPages = ['/login'];

  useEffect(() => {
    window.addEventListener('resize', detectHeightChange);
    return () => window.removeEventListener('resize', detectHeightChange);
  });

  useEffect(() => {
    if (activeMenu.includes('admin')) return;
    setActiveMenu(getDefaultActiveMenu(pathName));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName]);

  const getLogoutBoxData = () => {
    return defaultData;
  };

  const handleClose = () => {
    setOpenLogoutBox(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('simSchedule.selectedEquipment');
    signOut();
  };

  const isExternalUrl = (url) => url.substring(0, 3) === 'htt';

  const resetActiveMenus = () => {
    setActiveMenu('');
    setActiveSubMenu('');
  };

  const styleIsActive = {
    margin: '3rem 1.7rem 3rem'
  };

  function detectHeightChange() {
    const div = document.getElementsByClassName(
      'navigation-main-menu'
    )[0] as HTMLDivElement;
    let size = SIZES.LARGE;

    if (div.offsetHeight > BREAKS.LARGE) {
      size = SIZES.MEDIUM;
    }
    if (div.offsetHeight > BREAKS.MEDIUM) {
      size = SIZES.SMALL;
    }
    setSize(size);
  }

  const SubMenu = (param) => {
    return (
      <li
        className={
          param.id === activeMenu
            ? 'navigation-menu-item navigation-expand-more navigation-li-is-active'
            : 'navigation-menu-item navigation-expand-more'
        }
      >
        <NavLink
          to="#"
          style={({ isActive }) => (isActive ? styleIsActive : {})}
        >
          {param.icon && user ? (
            <AccountCircle />
          ) : (
            t(`Navigation.${param.title}`)
          )}{' '}
          <i className="icon-chevron-thin-down" />
        </NavLink>
        <ul className={`navigation-sub-menus ${size}`}>
          {param.subItems.map((subItem) => {
            //Disable sub menu item if sdo kill switch feature is enabled
            const shouldDisableSubItem =
              subItem.id === 'sub15' &&
              featureState.find(
                (item) => item.moduleId === 9 && item.isEnabled
              );

            return subItem.title === 'line' ? (
              <Divider light key={`${subItem.id}-${subItem.title}`} />
            ) : (
              <li
                key={`${subItem.id}-${subItem.title}`}
                style={{
                  paddingLeft:
                    subItem.id === 'admin13' || subItem.id === 'admin14'
                      ? '3rem'
                      : ''
                }}
                className={
                  subItem.id === activeSubMenu
                    ? 'navigation-submenu-is-active'
                    : ''
                }
              >
                {isExternalUrl(subItem.path) ? (
                  <Link to={{ pathname: subItem.path }} target="_blank">
                    {' '}
                    {t(`Navigation.${subItem.title}`)}{' '}
                  </Link>
                ) : (
                  <NavLink
                    className={shouldDisableSubItem ? 'disabled-nav-link' : ''}
                    onClick={(e) => {
                      if (shouldDisableSubItem) {
                        e.preventDefault(); // prevent navigation since menu is disabled
                      } else {
                        setActiveMenu(param.id);
                        setActiveSubMenu(subItem.id);
                        return subItem.id === 'logout'
                          ? setOpenLogoutBox(true)
                          : '';
                      }
                    }}
                    to={
                      subItem.id === 'logout' ? location.pathname : subItem.path
                    }
                  >
                    {t(`Navigation.${subItem.title}`)}
                    {shouldDisableSubItem && (
                      <Chip
                        label="Offline"
                        variant="outlined"
                        size="small"
                        className="offline-chip"
                      />
                    )}
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </li>
    );
  };

  const toggleMenu = (): any => {
    if (!user || pathName === '/google-access-policy') {
      return baseMenu;
    }

    let role = user ? user.attributes['custom:custom.roles'] : null;

    if (role && role.includes(',')) {
      let removeBrackets = role.substring(1, role.length - 1);
      const uniqueRoles = removeBrackets.split(', ');
      role = uniqueRoles.map((r) => {
        return r;
      });
    } else {
      role = [role];
    }
    if (role.includes(UserRoles.ADMIN)) {
      return [
        {
          title: 'admin',
          icon: false,
          path: '/admin',
          id: 'admin',
          subItems: [
            {
              title: 'adminPlanningBidding',
              path: '/admin',
              icon: false,
              id: 'admin11'
            },
            {
              title: 'line',
              path: '',
              icon: false,
              id: 'adminLine1'
            },
            {
              title: 'adminSpecialDaysOff',
              path: '/sdo-admin-report',
              icon: false,
              id: 'admin12'
            },
            {
              title: 'adminSdoReports',
              path: '/sdo-admin-report-details',
              icon: false,
              id: 'admin13'
            },
            {
              title: 'adminSdoEligibilityCriteria',
              path: '/sdo-admin-ec',
              icon: false,
              id: 'admin14'
            },
            {
              title: 'adminAppControl',
              path: '/admin-app-control',
              icon: false,
              id: 'admin15'
            }
          ]
        },
        ...menuItems
      ];
    }
    return menuItems;
  };

  const defaultData: AcDialogInterface = {
    title: t('logoutConfirmation.title'),
    description: t('logoutConfirmation.description'),
    actionLabel: t('logoutConfirmation.buttonText'),
    handleAction: handleLogout,
    secondaryActionLabel: t('logoutConfirmation.secondaryButtonText') ?? '',
    handleSecondaryAction: handleClose
  };

  return (
    <>
      {openLogoutBox && (
        <AcDialog
          open={openLogoutBox}
          onClose={handleClose}
          data={getLogoutBoxData()}
        />
      )}
      <header
        className={
          hideNavOnPages.includes(pathName) ? 'no-nav' : 'navigation-header'
        }
      >
        <div className="navigation-wrapper">
          <div className="navigation-header-logo">
            <Link
              to={pathName === '/google-access-policy' ? '/login' : '/'}
              onClick={() => resetActiveMenus()}
            >
              <img className="ac-logo" src={logo} alt="logo" />
            </Link>
            <Link
              to={pathName === '/google-access-policy' ? '/login' : '/'}
              className="title-crew-life"
              onClick={() => resetActiveMenus()}
            >
              {t('AppName')}
            </Link>
          </div>
          <div
            className="navigation-header-menus"
            style={{
              visibility:
                pathName === '/google-access-policy' ? 'hidden' : 'visible'
            }}
          >
            <nav className="navigation-main-nav">
              <ul className="navigation-main-menu">
                {toggleMenu().map((item, id) =>
                  item.subItems ? (
                    <SubMenu {...item} key={id} />
                  ) : (
                    <li
                      key={`${item.id}-${item.title}`}
                      className={
                        item.id === activeMenu
                          ? 'navigation-menu-item navigation-li-is-active'
                          : 'navigation-menu-item'
                      }
                    >
                      <NavLink
                        style={({ isActive }) =>
                          isActive ? styleIsActive : {}
                        }
                        onClick={() => {
                          setActiveMenu(item.id);
                          setActiveSubMenu('');
                          return item.id === 'login' ? signIn() : '';
                        }}
                        to={item.path}
                      >
                        {t(`Navigation.${item.title}`)}
                      </NavLink>
                    </li>
                  )
                )}
              </ul>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
}

export default Navigation;
